import { ReactNode, useState, useEffect, useCallback, useContext } from "react";
import {
  AssistantRuntimeProvider,
  useExternalStoreRuntime,
  type ThreadMessageLike,
  type AppendMessage,
} from "@assistant-ui/react";
import { useStytch } from "@stytch/react";
import { useAvaChatMessages } from "@/lib/api/ava";
import { DebugContext } from "@/routes/_authenticated/ava/$chatId";

interface AvaRuntimeProviderProps {
  children: ReactNode;
  chatId: string;
}

// Transform AVA backend message to assistant-ui ThreadMessageLike format
const transformAvaMessage = (avaMessage: any): ThreadMessageLike => {
  // Handle content transformation
  let content: ThreadMessageLike["content"];

  if (typeof avaMessage.content === "string") {
    // Simple text content
    content = [{ type: "text", text: avaMessage.content }];
  } else if (Array.isArray(avaMessage.content)) {
    // Already in the correct format or needs transformation
    const transformedParts = avaMessage.content.map((part: any) => {
      if (typeof part === "string") {
        return { type: "text", text: part };
      }
      if (part.type === "text") {
        return { type: "text", text: part.text || part.content || "" };
      }
      if (part.type === "tool-call") {
        const toolCallPart = {
          type: "tool-call",
          toolCallId: part.toolCallId || part._toolCallId || part.toolName,
          toolName: part.toolName,
          args: part.args || {},
          result: part.result, // Include result from streaming
          status: part.status || "pending", // Use status from backend, default to pending
        };

        // Debug log tool call status and result for monitoring
        console.log("🔧 Tool call status and result update", {
          toolName: toolCallPart.toolName,
          toolCallId: toolCallPart.toolCallId,
          status: toolCallPart.status,
          hasResult: !!toolCallPart.result,
          resultLength: toolCallPart.result?.length || 0,
          resultPreview:
            typeof toolCallPart.result === "string"
              ? toolCallPart.result.substring(0, 100) + "..."
              : toolCallPart.result,
        });

        return toolCallPart;
      }
      return part;
    });

    // Trust backend ordering - no manual sorting required
    // Backend's MessageTupleManager now provides proper ordering
    content = transformedParts;
  } else {
    // Fallback to empty text
    content = [{ type: "text", text: "" }];
  }

  return {
    role: avaMessage.role as "user" | "assistant",
    content,
  };
};

// Simple identity converter as the messages are already in the right format
const convertMessage = (message: ThreadMessageLike) => {
  return message;
};

export function AvaRuntimeProvider({
  children,
  chatId,
}: AvaRuntimeProviderProps) {
  const { session } = useStytch();
  const { data: existingMessages, isLoading } = useAvaChatMessages(chatId);
  const { isDebugMode } = useContext(DebugContext);

  // Helper function for conditional debug logging
  const debugLog = (message: string, data?: any) => {
    if (isDebugMode) {
      console.log(message, data);
    }
  };

  // State to manage messages and running state
  const [messages, setMessages] = useState<readonly ThreadMessageLike[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentAbortController, setCurrentAbortController] =
    useState<AbortController | null>(null);

  // Load existing messages when they arrive from API
  useEffect(() => {
    if (existingMessages && !isLoading) {
      debugLog("🔄 AVA Runtime: Loading existing messages", {
        messageCount: existingMessages.length,
        messages: existingMessages,
      });
      const transformedMessages = existingMessages.map(transformAvaMessage);
      setMessages(transformedMessages);
    }
  }, [existingMessages, isLoading]);

  // Cancel handler for ExternalStoreRuntime
  const onCancel = async () => {
    debugLog("🛑 AVA Runtime: Cancelling current request");
    setIsRunning(false);

    if (currentAbortController) {
      currentAbortController.abort();
      setCurrentAbortController(null);
    }

    // Remove thinking message
    setMessages((currentMessages) => {
      const filtered = currentMessages.filter(
        (msg, index) =>
          !(
            index === currentMessages.length - 1 &&
            msg.role === "assistant" &&
            Array.isArray(msg.content) &&
            msg.content.length === 0
          ),
      );
      debugLog("🛑 AVA Runtime: Removed thinking message", {
        originalCount: currentMessages.length,
        filteredCount: filtered.length,
      });
      return filtered;
    });
  };

  const onNew = async (message: AppendMessage) => {
    if (message.content.length !== 1 || message.content[0]?.type !== "text")
      throw new Error("Only text content is supported");

    debugLog("📤 AVA Runtime: New message from user", {
      text: message.content[0].text,
      chatId,
    });

    // Set running state
    setIsRunning(true);

    // Create new AbortController for this request
    const abortController = new AbortController();
    setCurrentAbortController(abortController);

    // Monitor abort signal
    abortController.signal.addEventListener("abort", () => {
      debugLog("🛑 AVA Runtime: Abort signal received");
      setIsRunning(false);
    });

    const userMessage: ThreadMessageLike = {
      role: "user",
      content: [{ type: "text", text: message.content[0].text }],
    };

    // Add user message and empty assistant message immediately for thinking state
    const thinkingAssistantMessage: ThreadMessageLike = {
      role: "assistant",
      content: [], // Empty content shows the thinking indicator
    };

    debugLog("💭 AVA Runtime: Adding thinking message");
    setMessages((currentMessages) => [
      ...currentMessages,
      userMessage,
      thinkingAssistantMessage,
    ]);

    // Convert assistant-ui messages to backend format
    const backendMessages = [...messages, userMessage].map((msg) => ({
      role: msg.role,
      contentParts: Array.isArray(msg.content)
        ? msg.content.map((part: any) => {
            if (part.type === "text") {
              return { type: "text", text: part.text };
            } else if (part.type === "tool-call") {
              return {
                type: "tool-call",
                toolCallId: part.toolCallId,
                toolName: part.toolName,
                args: part.args,
              };
            }
            return part;
          })
        : [{ type: "text", text: String(msg.content) }],
    }));

    try {
      debugLog("🌐 AVA Runtime: Starting streaming request", {
        url: `${import.meta.env.VITE_API_URL}/v2/ava/chats/${chatId}/stream-native`,
        messageCount: backendMessages.length,
      });

      // Stream request to our LangGraph backend
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/v2/ava/chats/${chatId}/stream-native`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${session?.getTokens()?.session_jwt || ""}`,
          },
          body: JSON.stringify({
            conversationHistory: backendMessages,
          }),
          signal: abortController.signal, // Pass the AbortSignal for cancellation support
        },
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      debugLog(
        "✅ AVA Runtime: Streaming response received, starting to process chunks",
      );

      // Handle streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      let assistantMessage: ThreadMessageLike = {
        role: "assistant",
        content: [],
      };

      if (reader) {
        let done = false;
        let chunkCount = 0;
        while (!done) {
          // Check if cancelled before reading next chunk
          if (abortController.signal.aborted) {
            debugLog("🛑 AVA Runtime: Abort detected while reading chunks");
            break;
          }

          const result = await reader.read();
          done = result.done;

          if (result.value) {
            chunkCount++;
            const chunk = decoder.decode(result.value, { stream: true });
            debugLog(`📦 AVA Runtime: Processing chunk ${chunkCount}`, {
              chunkLength: chunk.length,
              chunkPreview: chunk.substring(0, 100),
            });

            const lines = chunk.split("\n");

            for (const line of lines) {
              if (line.startsWith("data: ")) {
                try {
                  const data = JSON.parse(line.slice(6));
                  debugLog("📊 AVA Runtime: Parsed streaming data", data);

                  // Handle separate tool result events
                  if (data.type === "tool_result") {
                    debugLog("✅ AVA Runtime: Tool result received", {
                      toolCallId: data.toolCallId,
                      resultLength: data.result?.length || 0,
                      result: data.result,
                    });

                    // Tool results are now handled by backend's MessageTupleManager
                    // No need for complex frontend matching - just log for debugging
                    debugLog(
                      "✅ AVA Runtime: Tool result processed by backend",
                    );
                  }

                  // Handle content array (contains both tool calls and text)
                  if (data.content && Array.isArray(data.content)) {
                    debugLog("📝 AVA Runtime: Content received", {
                      contentParts: data.content.length,
                      content: data.content,
                      toolCalls: data.content.filter(
                        (part: any) => part.type === "tool-call",
                      ),
                      toolResults: data.content.filter(
                        (part: any) => part.type === "tool-result",
                      ),
                      toolStatuses: data.content
                        .filter((part: any) => part.type === "tool-call")
                        .map((part: any) => ({
                          name: part.toolName,
                          status: part.status,
                          hasResult: !!part.result,
                          resultLength: part.result?.length || 0,
                        })),
                    });

                    // Validate each content part has proper structure
                    const validContent = data.content.filter((part: any) => {
                      return part && typeof part === "object" && part.type;
                    });

                    if (validContent.length > 0) {
                      // Trust backend ordering - just use the content as provided
                      // Backend's MessageTupleManager ensures proper ordering
                      assistantMessage = {
                        role: "assistant",
                        content: validContent,
                      };

                      debugLog("📝 AVA Runtime: Using backend content as-is", {
                        contentParts: validContent.length,
                        totalContent: assistantMessage.content.length,
                      });

                      // Update the UI immediately
                      setMessages((currentMessages) => {
                        const updatedMessages = [...currentMessages];
                        if (
                          updatedMessages.length > 0 &&
                          updatedMessages[updatedMessages.length - 1].role ===
                            "assistant"
                        ) {
                          updatedMessages[updatedMessages.length - 1] =
                            assistantMessage;
                        } else {
                          updatedMessages.push(assistantMessage);
                        }
                        return updatedMessages;
                      });
                    }
                  }
                } catch (e) {
                  debugLog("⚠️ AVA Runtime: Failed to parse streaming chunk", {
                    line,
                    error: e,
                  });
                }
              }
            }
          }
        }
      }

      debugLog("🏁 AVA Runtime: Streaming completed", {
        finalContentLength: assistantMessage.content.length,
        finalContent: assistantMessage.content,
      });

      // Final update with complete assistant message (in case streaming missed something)
      if (assistantMessage.content.length > 0) {
        setMessages((currentMessages) => {
          const updatedMessages = [...currentMessages];
          // Replace the last message if it's an assistant message
          if (
            updatedMessages.length > 0 &&
            updatedMessages[updatedMessages.length - 1].role === "assistant"
          ) {
            updatedMessages[updatedMessages.length - 1] = assistantMessage;
          } else {
            updatedMessages.push(assistantMessage);
          }
          return updatedMessages;
        });
      }

      // Reset running state when request completes
      setIsRunning(false);
      setCurrentAbortController(null);
      debugLog("✅ AVA Runtime: Request completed successfully");
    } catch (error) {
      // Check if error is due to cancellation
      if (error instanceof Error && error.name === "AbortError") {
        debugLog("🛑 AVA Runtime: Request cancelled by user");
        // Remove the thinking message on cancellation
        setMessages((currentMessages) => {
          const filtered = currentMessages.filter(
            (msg, index) =>
              !(
                index === currentMessages.length - 1 &&
                msg.role === "assistant" &&
                Array.isArray(msg.content) &&
                msg.content.length === 0
              ),
          );
          return filtered;
        });
        return; // Don't show error message for cancellation
      }

      console.error("❌ AVA Runtime: Error in chat", error);

      // Reset running state on error
      setIsRunning(false);
      setCurrentAbortController(null);

      // Replace thinking message with error message
      const errorMessage: ThreadMessageLike = {
        role: "assistant",
        content: [
          {
            type: "text",
            text: "Sorry, I encountered an error. Please try again.",
          },
        ],
      };
      setMessages((currentMessages) => {
        const updatedMessages = [...currentMessages];
        // Replace the last message if it's an assistant message (the thinking message)
        if (
          updatedMessages.length > 0 &&
          updatedMessages[updatedMessages.length - 1].role === "assistant"
        ) {
          updatedMessages[updatedMessages.length - 1] = errorMessage;
        } else {
          updatedMessages.push(errorMessage);
        }
        return updatedMessages;
      });
    }
  };

  // Handler for adding tool results (user interactions)
  const onAddToolResult = useCallback(
    async (options: any) => {
      debugLog("🔧 AVA Runtime: Adding tool result", {
        toolCallId: options.toolCallId,
        resultLength: options.result?.length || 0,
      });

      // Backend now handles tool result integration properly
      // Just send the tool result to the server for continuation
      try {
        debugLog("🌐 AVA Runtime: Sending tool result to server");

        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/v2/ava/chats/${chatId}/stream-native`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `${session?.getTokens()?.session_jwt || ""}`,
            },
            body: JSON.stringify({
              messages: messages,
              toolResult: {
                toolCallId: options.toolCallId,
                result: options.result,
              },
            }),
          },
        );

        if (response.ok) {
          debugLog("✅ AVA Runtime: Tool result sent successfully");
        } else {
          console.error(
            "❌ AVA Runtime: Failed to send tool result",
            response.status,
          );
        }
      } catch (error) {
        console.error("❌ AVA Runtime: Error sending tool result:", error);
      }
    },
    [chatId, session, messages],
  );

  const runtime = useExternalStoreRuntime<ThreadMessageLike>({
    messages,
    setMessages,
    isRunning,
    onNew,
    onCancel,
    onAddToolResult, // ✅ Added the missing handler!
    convertMessage,
  });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
}
